import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { localSchedulesService } from '@/services';
import type { ApiLocalSchedule, CreateLocalScheduleRequest, UpdateLocalScheduleRequest } from '@/types/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface LocalScheduleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controllerId: string;
  controllerName: string;
  schedule?: ApiLocalSchedule | null;
  availableSlots: number[];
  protocolVersion?: 'v2' | 'v4';
  defaultSlotNumber?: number; // Slot específico para crear
}

// Helper: convertir bitmask a array de días
const bitmaskToDays = (mask: number): number[] => {
  const days: number[] = [];
  for (let i = 0; i < 7; i++) {
    if (mask & (1 << i)) {
      days.push(i);
    }
  }
  return days;
};

// Helper: convertir array de días a bitmask
const daysToBitmask = (days: number[]): number => {
  return days.reduce((mask, day) => mask | (1 << day), 0);
};

const daysOfWeek = [
  { value: 0, label: 'Lun', full: 'Lunes' },
  { value: 1, label: 'Mar', full: 'Martes' },
  { value: 2, label: 'Mié', full: 'Miércoles' },
  { value: 3, label: 'Jue', full: 'Jueves' },
  { value: 4, label: 'Vie', full: 'Viernes' },
  { value: 5, label: 'Sáb', full: 'Sábado' },
  { value: 6, label: 'Dom', full: 'Domingo' },
];

const schema = z.object({
  slotNumber: z.number().min(1).max(16),
  name: z.string().max(100).optional(),
  enabled: z.boolean().default(false),
  valveSelection: z.enum(['1', '2', '3']).transform(val => parseInt(val) as 1 | 2 | 3),
  startHour: z.number().min(0).max(23),
  startMinute: z.number().min(0).max(59),
  endHour: z.number().min(0).max(23),
  endMinute: z.number().min(0).max(59),
  days: z.array(z.number()).min(1, 'Seleccione al menos un día'),
  waterPulses: z.number().min(0).default(0),
  flowControlEnabled: z.boolean().default(false),
}).refine((data) => {
  const startMinutes = data.startHour * 60 + data.startMinute;
  const endMinutes = data.endHour * 60 + data.endMinute;
  return endMinutes > startMinutes;
}, {
  message: 'La hora de fin debe ser posterior a la hora de inicio',
  path: ['endHour'],
});

type FormData = z.infer<typeof schema>;

export function LocalScheduleFormDialog({
  open,
  onOpenChange,
  controllerId,
  controllerName,
  schedule,
  availableSlots,
  protocolVersion = 'v2',
  defaultSlotNumber,
}: LocalScheduleFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const maxSlots = protocolVersion === 'v4' ? 16 : 10;

  const isEditing = !!schedule;

  // Parsear horario de schedule existente
  const parseTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return { hours: hours || 8, minutes: minutes || 0 };
  };

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      slotNumber: schedule?.slotNumber || defaultSlotNumber || availableSlots[0] || 1,
      name: schedule?.name || '',
      enabled: schedule?.enabled ?? false,
      valveSelection: schedule?.valveSelection ? String(schedule.valveSelection) as '1' | '2' | '3' : '1',
      startHour: schedule ? parseTime(schedule.startTime).hours : 8,
      startMinute: schedule ? parseTime(schedule.startTime).minutes : 0,
      endHour: schedule ? parseTime(schedule.endTime).hours : 8,
      endMinute: schedule ? parseTime(schedule.endTime).minutes : 30,
      days: schedule ? bitmaskToDays(schedule.daysOfWeekMask) : [0, 1, 2, 3, 4], // Lun-Vie por defecto
      waterPulses: schedule?.waterPulses || 0,
      flowControlEnabled: schedule?.flowControlEnabled || false,
    },
  });

  // Resetear formulario cuando cambia schedule o se abre/cierra el diálogo
  useEffect(() => {
    if (!open) return; // No resetear si el diálogo está cerrado
    
    if (schedule) {
      const startTime = parseTime(schedule.startTime);
      const endTime = parseTime(schedule.endTime);
      form.reset({
        slotNumber: schedule.slotNumber,
        name: schedule.name || '',
        enabled: schedule.enabled,
        valveSelection: String(schedule.valveSelection) as '1' | '2' | '3',
        startHour: startTime.hours,
        startMinute: startTime.minutes,
        endHour: endTime.hours,
        endMinute: endTime.minutes,
        days: bitmaskToDays(schedule.daysOfWeekMask),
        waterPulses: schedule.waterPulses || 0,
        flowControlEnabled: schedule.flowControlEnabled || false,
      });
    } else {
      form.reset({
        slotNumber: defaultSlotNumber || availableSlots[0] || 1,
        name: '',
        enabled: false,
        valveSelection: '1',
        startHour: 8,
        startMinute: 0,
        endHour: 8,
        endMinute: 30,
        days: [0, 1, 2, 3, 4],
        waterPulses: 0,
        flowControlEnabled: false,
      });
    }
  }, [schedule, availableSlots, defaultSlotNumber, open, form]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateLocalScheduleRequest) => {
      const response = await localSchedulesService.create(controllerId, data);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al crear programación');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-schedules', controllerId] });
      queryClient.invalidateQueries({ queryKey: ['schedule-verification', controllerId] });
      toast({
        title: 'Programación creada',
        description:
          'Programación guardada. Pendiente de confirmación en el dispositivo.',
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateLocalScheduleRequest) => {
      if (!schedule) throw new Error('No hay programación seleccionada');
      const response = await localSchedulesService.update(controllerId, schedule.id, data);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al actualizar programación');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-schedules', controllerId] });
      queryClient.invalidateQueries({ queryKey: ['schedule-verification', controllerId] });
      toast({
        title: 'Programación actualizada',
        description:
          'Cambios guardados. Pendiente de confirmación en el dispositivo.',
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: FormData) => {
    const daysMask = daysToBitmask(data.days);
    
    if (isEditing) {
      const updateData: UpdateLocalScheduleRequest = {
        name: data.name || undefined,
        enabled: data.enabled,
        valveSelection: data.valveSelection,
        startHour: data.startHour,
        startMinute: data.startMinute,
        endHour: data.endHour,
        endMinute: data.endMinute,
        daysOfWeek: daysMask,
        waterPulses: data.waterPulses || undefined,
        flowControlEnabled: data.flowControlEnabled,
      };
      updateMutation.mutate(updateData);
    } else {
      const createData: CreateLocalScheduleRequest = {
        slotNumber: data.slotNumber,
        name: data.name || undefined,
        enabled: data.enabled,
        valveSelection: data.valveSelection,
        startHour: data.startHour,
        startMinute: data.startMinute,
        endHour: data.endHour,
        endMinute: data.endMinute,
        daysOfWeek: daysMask,
        waterPulses: data.waterPulses || undefined,
        flowControlEnabled: data.flowControlEnabled,
      };
      createMutation.mutate(createData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Programación Local' : 'Nueva Programación Local'}
          </DialogTitle>
          <DialogDescription>
            {controllerName} - {isEditing ? 'Modificar programación' : 'Crear nueva programación local'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Slot Number (solo al crear) */}
            {!isEditing && (
              <FormField
                control={form.control}
                name="slotNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slot</FormLabel>
                    <FormControl>
                      <div className="relative">
                        {availableSlots.length > 0 ? (
                          <select
                            {...field}
                            value={field.value}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            className={cn(
                              "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer"
                            )}
                          >
                            {availableSlots.map((slot) => (
                              <option key={slot} value={slot}>
                                Slot {slot}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="flex h-10 w-full items-center justify-center rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive">
                            No hay slots disponibles
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormDescription>
                      {availableSlots.length > 0 
                        ? `Seleccione un slot disponible (${availableSlots.length} disponibles de ${maxSlots} totales)${defaultSlotNumber ? ` - Slot ${defaultSlotNumber} preseleccionado` : ''}`
                        : `Todos los slots están ocupados (${maxSlots}/${maxSlots})`}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Nombre */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre (opcional)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ej: Riego matutino" maxLength={100} />
                  </FormControl>
                  <FormDescription>
                    Nombre descriptivo para identificar la programación
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Activar */}
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel>Activar programación</FormLabel>
                    <FormDescription>
                      La programación se ejecutará solo si está activada
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Selección de válvulas */}
            <FormField
              control={form.control}
              name="valveSelection"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Válvulas</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <select
                        value={field.value}
                        onChange={field.onChange}
                        className={cn(
                          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer"
                        )}
                      >
                        <option value="1">Válvula 1</option>
                        <option value="2">Válvula 2</option>
                        <option value="3">Ambas válvulas</option>
                      </select>
                    </div>
                  </FormControl>
                  <FormDescription>
                    Seleccione qué válvula(s) se activarán
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Horario */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora de inicio</Label>
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="startHour"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="23"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            placeholder="08"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="startMinute"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="59"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            placeholder="00"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Hora de fin</Label>
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="endHour"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="23"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            placeholder="08"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="endMinute"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="59"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            placeholder="30"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Días de la semana */}
            <FormField
              control={form.control}
              name="days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Días de la semana</FormLabel>
                  <div className="grid grid-cols-7 gap-2">
                    {daysOfWeek.map((day) => (
                      <FormField
                        key={day.value}
                        control={form.control}
                        name="days"
                        render={() => (
                          <FormItem className="flex flex-col items-center space-y-2">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(day.value)}
                                onCheckedChange={(checked) => {
                                  const currentDays = field.value || [];
                                  if (checked) {
                                    field.onChange([...currentDays, day.value]);
                                  } else {
                                    field.onChange(currentDays.filter((d) => d !== day.value));
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-xs font-normal cursor-pointer">
                              {day.label}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Límite de pulsos */}
            <FormField
              control={form.control}
              name="waterPulses"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Límite de pulsos (opcional)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      placeholder="0 = sin límite"
                    />
                  </FormControl>
                  <FormDescription>
                    Establece un límite de pulsos de agua. 0 = sin límite
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Control de flujo */}
            <FormField
              control={form.control}
              name="flowControlEnabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel>Control de flujo</FormLabel>
                    <FormDescription>
                      Activar control de flujo para esta programación
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
